import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translate } from '../../locales';
import type { TranslationKey } from '../../locales/types';
import './Popconfirm.css';

export interface PopconfirmProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  subtitle?: string;
  description?: string;
  cancelText?: string;
  okText?: string;
  okButtonVariant?: 'danger' | 'primary' | 'default';
  placement?: 'center' | 'top' | 'bottom' | 'left' | 'right';
  style?: React.CSSProperties;
}

const Popconfirm: React.FC<PopconfirmProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  subtitle,
  cancelText,
  okText,
  okButtonVariant = 'danger',
  placement = 'center',
  style = {},
}) => {
  const { language } = useLanguage();
  const t = (key: TranslationKey) => translate(key, language as 'en' | 'ua');
  if (!open) return null;
  let cardStyle: React.CSSProperties = { ...style };
  let backdropStyle: React.CSSProperties = {};
  let backdropClass = 'popconfirm-backdrop';
  if (placement !== 'center') backdropClass += ' popconfirm-backdrop-abs';
  if (placement === 'center') {
    backdropStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.18)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' };
    cardStyle = { ...cardStyle };
  } else {
    backdropStyle = {};
    cardStyle = { position: 'absolute', zIndex: 100, pointerEvents: 'auto', ...cardStyle };
  if (placement === 'top') cardStyle = { ...cardStyle, bottom: '100%', left: '70%', transform: 'translateX(-50%)', marginBottom: 8 };
    if (placement === 'bottom') cardStyle = { ...cardStyle, top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: 8 };
    if (placement === 'left') cardStyle = { ...cardStyle, right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: 8 };
    if (placement === 'right') cardStyle = { ...cardStyle, left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: 8 };
  }
  return (
    <div className={backdropClass} style={backdropStyle}>
      <div className="popconfirm-card" style={cardStyle}>
        <div className="popconfirm-header">
          <div className="popconfirm-title">{title || t('popconfirm.title')}</div>
          {(subtitle || t('popconfirm.subtitle')) && <div className="popconfirm-subtitle">{subtitle || t('popconfirm.subtitle')}</div>}
        </div>
        <div className="popconfirm-content">
          <div className="popconfirm-actions">
            <button 
                className="popconfirm-btn popconfirm-cancel" 
                onClick={onClose}>{cancelText || t('popconfirm.no')}</button>
            <button
                className={`popconfirm-btn popconfirm-ok ${okButtonVariant}`}
                onClick={onConfirm}
            >
                {okText || t('popconfirm.yes')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Popconfirm;
